import onetime from "@-/com.npmjs.onetime";
import {
  getOnboardingGlobeGeometry,
  onboardingGlobeRotation,
  toUnitSphere,
} from "@-/dev.improve.haniverse.routes.nav.app.onboarding/onboarding-globe-geometry";
import {
  onboardingGlobePerspective,
  onboardingGlobeRingBounds,
} from "@-/dev.improve.haniverse.routes.nav.app.onboarding/onboarding-globe-paths";
import {
  lerp,
  smootherstep,
  welcomeGalaxyHeadlineHeight,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

/** The native splash's measured box, shared with the landing's first frame. */
export const WELCOME_HANI_SPLASH_SIZE = 96;

/** The native auth's upper-cap view, with north up and no screen-space roll. */
export const getWelcomeHaniGlobeView = onetime(() => {
  const geometry = getOnboardingGlobeGeometry();
  const camera = {
    distance: 100,
    rotation: onboardingGlobeRotation({ latitude: -24, longitude: geometry.chinaCenter.longitude }),
  };
  const { limbRadius, safeDistance } = onboardingGlobePerspective(camera.distance);
  const project = (longitude: number, latitude: number) => {
    const point = toUnitSphere([longitude, latitude]);
    const [x = 0, y = 0, z = 0] = point;
    const m = camera.rotation;
    const depth = (m[6] ?? 0) * x + (m[7] ?? 0) * y + (m[8] ?? 0) * z;
    const scale = (safeDistance - 1) / (safeDistance - depth) / limbRadius;
    return {
      x: ((m[0] ?? 0) * x + (m[1] ?? 0) * y + (m[2] ?? 0) * z) * scale,
      y: ((m[3] ?? 0) * x + (m[4] ?? 0) * y + (m[5] ?? 0) * z) * scale,
    };
  };
  return {
    bounds: onboardingGlobeRingBounds(geometry.china, camera),
    camera,
    project,
    // Just inside the low northern border, between Xinjiang and the northeast.
    seating: project(geometry.chinaCenter.longitude, 41.5),
  };
});

/** Frame the native globe view as a shallow cap on the wider web welcome. */
function welcomeHaniWebCap(height: number, width: number) {
  const {
    bounds: [minX, minY, maxX, maxY],
    seating,
  } = getWelcomeHaniGlobeView();
  const radius = Math.max(1, width * 1.25, height * 0.55);
  const horizonY = height * (width > height ? 0.56 : 0.72);
  const countryWidth = maxX - minX;
  const countryHeight = maxY - minY;
  // Preserve the phone reference's Hani-to-geography proportions while fitting wide screens.
  const haniPerRadius = 0.78 / 1.25;
  const maxHaniSize = Math.min(height * 0.55, width * 0.78);
  const countryTop = (scale: number) => {
    const halfWidth = (countryWidth * scale) / 2;
    return horizonY + 24 + radius - Math.sqrt(radius * radius - halfWidth * halfWidth);
  };
  let low = 0;
  let high = Math.min(radius, (width * 0.9) / countryWidth, maxHaniSize / haniPerRadius);
  // Fit the complete outline into the curved cap, keeping one uniform spherical projection.
  // This search is monotonic and bounded: it never changes the camera or distorts China.
  for (let step = 0; step < 24; step += 1) {
    const middle = (low + high) / 2;
    if (countryTop(middle) + countryHeight * middle <= height - 24) {
      low = middle;
    } else {
      high = middle;
    }
  }
  const geographyRadius = Math.max(1, low);
  const geographyDisk = {
    centerX: width / 2 - ((minX + maxX) / 2) * geographyRadius,
    centerY: countryTop(geographyRadius) - minY * geographyRadius,
    radius: geographyRadius,
  };
  const seatingY = geographyDisk.centerY + seating.y * geographyRadius;
  // The phone's Hani occupies 0.78 screen widths on an Earth of radius 1.25 screen widths.
  const haniSize = geographyRadius * haniPerRadius;
  return {
    centerX: width / 2,
    centerY: horizonY + radius,
    geographyDisk,
    haniCenterX: geographyDisk.centerX + seating.x * geographyRadius,
    haniSize,
    horizonY,
    radius,
    seatingY,
  };
}

/** Characters gain opacity in place while Hani lands and Earth expands. */
export function welcomeGalaxyFormationOpacity(t: number): number {
  "worklet";
  return smootherstep(-1.35, 2.4, t);
}

/** Shared screen geometry: Hani sits onto the curved Earth, above the account controls. */
export function welcomeHaniWorldLayout({
  bottomInset,
  buttonDiameter,
  isWeb = false,
  sideInset = 0,
  viewport,
}: {
  bottomInset: number;
  buttonDiameter: number;
  isWeb?: boolean;
  sideInset?: number;
  viewport: WelcomeGalaxyViewport;
}) {
  const { height, width } = viewport;
  const stageWidth = viewport.stageWidth ?? width;
  const isSplit = width > height;
  // Match the overlay's 9pt margins/gaps and its single-line, width-fitted tagline.
  const taglineHeight = Math.ceil((((width - 18) * 0.96) / 22.856) * 1.4);
  const controlsTop = height - bottomInset - 9 - 2 * buttonDiameter - 18 - taglineHeight;
  const headlineHeight = welcomeGalaxyHeadlineHeight(viewport, sideInset);
  const headlineTop = (isSplit ? height - bottomInset - 18 : controlsTop - 9) - headlineHeight;
  const haniSize = Math.max(
    0,
    Math.min(stageWidth * 0.78, height * (isSplit ? 0.48 : 0.37), headlineTop - 52),
  );
  const { bounds, seating } = getWelcomeHaniGlobeView();
  const [minX, minY, maxX, maxY] = bounds;
  const initialSeat = headlineTop - 12;
  const radius = Math.max(
    1,
    Math.min(
      Math.max(stageWidth * 1.25, height * 0.55),
      (height - initialSeat - 24) / (maxY - seating.y),
      (initialSeat - 24) / (seating.y - minY),
    ),
  );
  // Reuse the phone's oversized globe, with its geography on that same sphere. Move the
  // entire globe to put the northern dip under Hani; keep the camera fixed across sizes.
  const seatingY = initialSeat;
  const centerX = stageWidth / 2 - ((minX + maxX) / 2) * radius;
  const centerY = seatingY - seating.y * radius;
  const horizonY = centerY - radius;
  const frame = isWeb
    ? welcomeHaniWebCap(height, stageWidth)
    : {
        centerX,
        centerY,
        geographyDisk: { centerX, centerY, radius },
        haniCenterX: centerX + seating.x * radius,
        haniSize,
        horizonY,
        radius,
        seatingY,
      };
  return {
    ...frame,
    controlsTop,
    headlineHeight,
    headlineTop,
    // The generated cutout has 4% transparent padding below the contact plane.
    haniTop: frame.seatingY - frame.haniSize * 0.96,
    stageWidth,
  };
}

/** One bounded, reversible choreography; the feet descend as Hani grows out of the splash. */
export function welcomeHaniWorldFrame(
  t: number,
  layout: ReturnType<typeof welcomeHaniWorldLayout>,
  viewport: WelcomeGalaxyViewport,
  fromSplash: boolean,
) {
  "worklet";
  const growth = smootherstep(-2, 0.35, t);
  const landing = smootherstep(-1.65, 0.85, t);
  const haniScale = fromSplash
    ? lerp(WELCOME_HANI_SPLASH_SIZE / Math.max(1, layout.haniSize), 1, growth)
    : 1;
  const feetY = fromSplash
    ? lerp(
        viewport.height / 2 + WELCOME_HANI_SPLASH_SIZE * 0.46,
        layout.seatingY,
        landing * landing,
      )
    : layout.seatingY;
  const globeScale = lerp(0.12, 1, smootherstep(-1.9, 1.3, t));
  const horizonY = lerp(viewport.height / 2 + 100, layout.horizonY, smootherstep(-1.9, 1.3, t));
  return {
    globeOpacity: smootherstep(-2, -1.1, t),
    globeScale,
    globeTranslateY: horizonY + layout.radius * globeScale - layout.centerY,
    haniScale,
    haniTranslateX: fromSplash ? (viewport.width / 2 - layout.haniCenterX) * (1 - growth) : 0,
    haniTranslateY:
      feetY - layout.haniSize * 0.46 * haniScale - (layout.haniTop + layout.haniSize / 2),
    portraitOpacity: fromSplash ? smootherstep(-1.9, -0.7, t) : smootherstep(-2, 0.3, t),
  };
}
